import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { FaMagnifyingGlass } from 'react-icons/fa6';
import { FaPlus } from 'react-icons/fa';
import { RiHistoryLine } from 'react-icons/ri';
import { MdModeEdit } from 'react-icons/md';
import { styled } from 'styled-components';

import { AddButton, TableActionButton, OrderStatusLegend } from '../styles/StyledComponents';
import { checkPermission } from '../utils/permissions';
import { useAppContext } from '../contextApi/context';
import TableContainer from '../components/table/TableContainer';
import Spinner from '../components/shared/Spinner';
import { formatDateAndTime } from '../utils/dates';
import { searchWorkOrders, getMaintenanceTypes } from '../services/OrdersService';
import { searchUsersByName } from '../services/UsersService';
import { ORDER_STATUS, getOrderStatusConfig } from '../constants/orderStatus';
import { getClientTypeLabel, getClientTypeOptions } from '../constants/orderClientTypes';

const Orders = () => {
    // ----- Context/Hooks
    const navigate = useNavigate();
    const location = useLocation();
    const { loggedUser } = useAppContext();

    // Ref para controlar que el mensaje se procese una sola vez
    const hasProcessedSuccessMessage = useRef(false);

    // ----- State
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [maintenanceTypes, setMaintenanceTypes] = useState([]);
    const [availableUsersForFilter, setAvailableUsersForFilter] = useState([]);
    const [filterTimer, setFilterTimer] = useState(null);

    // Estado para tabla de órdenes propias
    const [ownOrdersData, setOwnOrdersData] = useState([]);
    const [ownOrdersLoading, setOwnOrdersLoading] = useState(false);
    const [ownOrdersPage, setOwnOrdersPage] = useState(1);
    const [ownOrdersPerPage, setOwnOrdersPerPage] = useState(10);
    const [ownOrdersTotalRows, setOwnOrdersTotalRows] = useState(0);

    // ----- Effects

    // Efecto 1: Procesar mensaje de navegación y limpiar estado
    useEffect(() => {
        if (!hasProcessedSuccessMessage.current && location.state?.showSuccessMessage && location.state?.justCreated) {
            hasProcessedSuccessMessage.current = true;
            setShowSuccessMessage(true);

            // Limpiar el state de navegación
            navigate(location.pathname, {
                replace: true,
                state: {
                    ...location.state,
                    showSuccessMessage: undefined,
                    justCreated: undefined,
                },
            });
        }
    }, [location.state, location.pathname, navigate]);

    // Efecto 2: Auto-ocultar mensaje de éxito después de 5 segundos
    useEffect(() => {
        if (showSuccessMessage) {
            const timer = setTimeout(() => {
                setShowSuccessMessage(false);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [showSuccessMessage]);

    // Cargar tipos de mantenimiento al montar el componente
    useEffect(() => {
        const loadMaintenanceTypes = async () => {
            try {
                const types = await getMaintenanceTypes(loggedUser.token);
                setMaintenanceTypes(types);
            } catch (error) {
                console.error('Error al cargar tipos de mantenimiento:', error);
            }
        };

        if (loggedUser?.token) {
            loadMaintenanceTypes();
        }
    }, [loggedUser]);

    // Cargar órdenes propias cuando corresponda
    useEffect(() => {
        if (
            loggedUser &&
            checkPermission(loggedUser.permissions, 'add_own_work_orders') &&
            !checkPermission(loggedUser.permissions, 'view_work_orders')
        ) {
            fetchOwnOrders();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedUser, ownOrdersPage, ownOrdersPerPage]);

    // ----- Data Handler
    const fetchWorkOrdersData = useCallback(async (token, page = 1, perPage = 10, sortField = null, filters = []) => {
        try {
            // Transformar filtros del formato del componente al formato de la API
            const filtersData = filters
                .filter((filter) => filter.value !== '' && filter.value !== null && filter.value !== undefined)
                .map((filter) => {
                    // Manejar diferentes tipos de valores según el tipo de filtro
                    let values;

                    // Para filtros de rango de fechas
                    if (filter.operation === 'between' && Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para filtros múltiples (select múltiple, tags, etc.)
                    else if (Array.isArray(filter.value)) {
                        values = filter.value;
                    }
                    // Para valores únicos
                    else {
                        values = [filter.value];
                    }
                    if (filter.columnName === 'orderNumber' && values.length > 0) {
                        values = values.map((val) => {
                            const numValue = Number(val);
                            return isNaN(numValue) ? val : numValue;
                        });
                    }

                    return {
                        columnName: filter.columnName,
                        operation: filter.operation,
                        values: values,
                    };
                });

            // Verificar si hay filtro de status aplicado por el usuario
            const userStatusFilter = filtersData.find((filter) => filter.columnName === 'status');

            if (!userStatusFilter) {
                // Si no hay filtro de status del usuario, aplicar filtro por defecto
                const defaultStatusFilter = {
                    columnName: 'status',
                    operation: 'in',
                    values: [ORDER_STATUS.CREATED.value, ORDER_STATUS.APPROVED.value, ORDER_STATUS.MAINTENANCE.value],
                };
                filtersData.push(defaultStatusFilter);
            }

            // Establecer ordenamiento por defecto si no se especifica
            const sortParam = sortField || 'requestedDate,desc';

            // Realizar la llamada a la API con los filtros transformados
            const response = await searchWorkOrders(token, page, perPage, sortParam, filtersData);

            // Verificar si la respuesta tiene la estructura esperada
            if (response && response.content) {
                return {
                    content: response.content,
                    totalElements: response.totalElements,
                    currentPage: page,
                    totalPages: response.totalPages,
                    number: response.number,
                    size: response.size,
                    first: response.first,
                    last: response.last,
                };
            } else {
                return {
                    content: [],
                    totalElements: 0,
                    currentPage: page,
                    totalPages: 0,
                };
            }
        } catch (error) {
            return {
                content: [],
                totalElements: 0,
                currentPage: page,
                totalPages: 0,
            };
        }
    }, []);

    // Función para cargar órdenes propias
    const fetchOwnOrders = async () => {
        setOwnOrdersLoading(true);
        try {
            const filtersData = [];

            // Filtro por username del usuario logueado
            const usernameFilter = {
                columnName: 'requester.username',
                operation: 'equals',
                values: [loggedUser.username],
            };
            filtersData.push(usernameFilter);

            // Filtro de estados: solo Creadas y Aprobadas
            const statusFilter = {
                columnName: 'status',
                operation: 'in',
                values: [ORDER_STATUS.CREATED.value, ORDER_STATUS.APPROVED.value],
            };
            filtersData.push(statusFilter);

            const sortParam = 'requestedDate,desc';
            const response = await searchWorkOrders(loggedUser.token, ownOrdersPage, ownOrdersPerPage, sortParam, filtersData);

            if (response && response.content) {
                setOwnOrdersData(response.content);
                setOwnOrdersTotalRows(response.totalElements);
            } else {
                setOwnOrdersData([]);
                setOwnOrdersTotalRows(0);
            }
        } catch (error) {
            console.error('Error al cargar órdenes propias:', error);
            setOwnOrdersData([]);
            setOwnOrdersTotalRows(0);
        }
        setOwnOrdersLoading(false);
    };

    // ----- Actions

    // Manejar paginación de órdenes propias
    const handleOwnOrdersPageChange = (newPage) => {
        setOwnOrdersPage(newPage);
    };

    const handleOwnOrdersPerPageChange = (e) => {
        setOwnOrdersPerPage(Number(e.target.value));
        setOwnOrdersPage(1);
    };

    // Función para buscar usuarios en el filtro
    const handleSearchUsersForFilter = useCallback(
        async (searchText) => {
            if (!searchText || searchText.trim() === '') {
                setAvailableUsersForFilter([]);
                return;
            }

            clearTimeout(filterTimer);

            const newTimer = setTimeout(async () => {
                try {
                    const response = await searchUsersByName(loggedUser.token, searchText);
                    const formattedUsers =
                        response?.map((user) => ({
                            value: user.id,
                            label: `${user.firstName} ${user.lastName} (${user.username})`,
                        })) || [];
                    setAvailableUsersForFilter(formattedUsers);
                } catch (error) {
                    console.error('Error al buscar usuarios:', error);
                    setAvailableUsersForFilter([]);
                }
            }, 500);

            setFilterTimer(newTimer);
        },
        [loggedUser, filterTimer]
    );

    // ----- Helper Functions
    const getOrderNumberWithStatus = (orderNumber, status) => {
        const config = getOrderStatusConfig(status);

        return (
            <div
                className='position-relative'
                style={{
                    paddingLeft: '20px',
                    display: 'inline-block',
                }}
            >
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Estado: {config.label}</Tooltip>}
                    placement='top'
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '0',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: config.color,
                            transform: 'translateY(-50%)',
                        }}
                    />
                </OverlayTrigger>
                <span>{orderNumber}</span>
            </div>
        );
    };

    // ----- Table Configuration
    const Actions = ({ row }) => (
        <div className='d-flex'>
            {checkPermission(loggedUser.permissions, 'view_work_orders') && (
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Ver detalles de la orden</Tooltip>}
                    placement='top'
                >
                    <TableActionButton className='btn btn-primary me-1'>
                        <Link
                            to={`/ordenes/detalles/${row.orderNumber}`}
                            state={{
                                orderId: row.id,
                            }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            <FaMagnifyingGlass />
                            Detalles
                        </Link>
                    </TableActionButton>
                </OverlayTrigger>
            )}

            {checkPermission(loggedUser.permissions, 'manage_work_orders') && (
                <OverlayTrigger
                    delay={{ hide: 200, show: 300 }}
                    overlay={(props) => <Tooltip {...props}>Editar orden de trabajo</Tooltip>}
                    placement='top'
                >
                    <TableActionButton className='btn btn-primary me-1'>
                        <Link
                            to={`/ordenes/${row.orderNumber}`}
                            state={{
                                orderId: row.id,
                            }}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                        >
                            <MdModeEdit />
                            Editar
                        </Link>
                    </TableActionButton>
                </OverlayTrigger>
            )}
        </div>
    );

    const columns = [
        {
            name: 'N° Orden',
            selector: (row) => getOrderNumberWithStatus(row.orderNumber, row.status),
            sortable: true,
            sortField: 'orderNumber',
            width: '120px',
        },
        {
            name: 'Fecha de solicitud',
            selector: (row) => formatDateAndTime(row.requestedDate),
            sortable: true,
            sortField: 'requestedDate',
            width: '170px',
        },
        {
            name: 'Solicitante',
            selector: (row) => row.requesterUser,
            sortField: 'requesterUser',
            width: '270px',
        },
        {
            name: 'Placa',
            selector: (row) => row.vehiclePlate,
            sortable: true,
            sortField: 'vehiclePlate',
            width: '150px',
        },
        {
            name: 'Tipo de mantenimiento',
            selector: (row) => row.maintenanceTypeName,
            sortable: true,
            sortField: 'maintenanceTypeName',
            width: '205px',
        },
        {
            name: 'Tipo de cliente',
            selector: (row) => getClientTypeLabel(row.orderClientType),
            sortable: true,
            sortField: 'orderClientType',
            width: '150px',
        },
        {
            name: 'Acciones',
            width: '200px',
            cell: (row) => <Actions row={row} />,
        },
    ];

    // Filtros con useMemo para actualización dinámica
    const filters = useMemo(
        () => [
            {
                column: 'orderNumber',
                input: 'text',
                label: 'N° Orden',
                operation: 'equals',
                length: 20,
            },
            {
                column: 'status',
                input: 'select',
                label: 'Estado',
                options: [
                    { value: ORDER_STATUS.CREATED.value, text: ORDER_STATUS.CREATED.label },
                    { value: ORDER_STATUS.APPROVED.value, text: ORDER_STATUS.APPROVED.label },
                    { value: ORDER_STATUS.MAINTENANCE.value, text: ORDER_STATUS.MAINTENANCE.label },
                ],
                operation: 'equals',
            },
            {
                column: 'requester.id',
                input: 'autocomplete',
                label: 'Solicitante',
                operation: 'equals',
                placeholder: 'Buscar por nombre, apellido o usuario',
                searchHandler: handleSearchUsersForFilter,
                options: availableUsersForFilter,
                noOptionsText: 'Escribe para buscar',
            },
            {
                column: 'vehicle.plate',
                input: 'text',
                label: 'Placa',
                operation: 'like',
                length: 20,
            },
            {
                column: 'maintenanceType.name',
                input: 'select',
                label: 'Tipo de mantenimiento',
                options: maintenanceTypes.map((type) => ({
                    value: type.name,
                    text: type.name,
                })),
                operation: 'equals',
            },
            {
                column: 'orderClientType',
                input: 'select',
                label: 'Tipo de cliente',
                options: getClientTypeOptions(),
                operation: 'equals',
            },
            {
                column: 'requestedDate',
                input: 'dates',
                label: 'Fecha de solicitud',
                operation: 'between',
            },
        ],
        [maintenanceTypes, availableUsersForFilter, handleSearchUsersForFilter]
    );

    // Renderizar contenido según permisos
    const renderTableContent = () => {
        if (!loggedUser) {
            return null;
        }

        if (checkPermission(loggedUser.permissions, 'view_work_orders')) {
            return <TableContainer columns={columns} fetchDataHandler={fetchWorkOrdersData} filters={filters} />;
        } else if (checkPermission(loggedUser.permissions, 'add_own_work_orders')) {
            return (
                <div className='mt-4'>
                    {ownOrdersLoading ? (
                        <div className='text-center my-5'>
                            <Spinner />
                        </div>
                    ) : ownOrdersData.length === 0 ? (
                        <p className='text-center my-5'>No hay datos que mostrar</p>
                    ) : (
                        <>
                            <ResponsiveTable>
                                <thead>
                                    <tr>
                                        <th>N° Orden</th>
                                        <th>Placa</th>
                                        <th>Observaciones</th>
                                        <th>Instrucciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ownOrdersData.map((order) => (
                                        <tr key={order.id}>
                                            <td data-label='N° Orden'>{getOrderNumberWithStatus(order.orderNumber, order.status)}</td>
                                            <td data-label='Placa'>{order.vehiclePlate}</td>
                                            <td data-label='Observaciones'>{order.observations || 'Sin observaciones'}</td>
                                            <td data-label='Instrucciones'>{order.instructions || 'Sin instrucciones'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </ResponsiveTable>

                            <PaginationContainer>
                                <PaginationInfo>
                                    Mostrando {(ownOrdersPage - 1) * ownOrdersPerPage + 1} a{' '}
                                    {Math.min(ownOrdersPage * ownOrdersPerPage, ownOrdersTotalRows)} de {ownOrdersTotalRows} registros
                                </PaginationInfo>
                                <PaginationControls>
                                    <select value={ownOrdersPerPage} onChange={handleOwnOrdersPerPageChange}>
                                        <option value={10}>10</option>
                                        <option value={15}>15</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                        <option value={50}>50</option>
                                    </select>
                                    <button disabled={ownOrdersPage === 1} onClick={() => handleOwnOrdersPageChange(ownOrdersPage - 1)}>
                                        Anterior
                                    </button>
                                    <span>
                                        Página {ownOrdersPage} de {Math.ceil(ownOrdersTotalRows / ownOrdersPerPage)}
                                    </span>
                                    <button
                                        disabled={ownOrdersPage >= Math.ceil(ownOrdersTotalRows / ownOrdersPerPage)}
                                        onClick={() => handleOwnOrdersPageChange(ownOrdersPage + 1)}
                                    >
                                        Siguiente
                                    </button>
                                </PaginationControls>
                            </PaginationContainer>
                        </>
                    )}
                </div>
            );
        }
        return null;
    };

    // ----- Render
    return (
        <div className='container mt-5'>
            <h1>
                {loggedUser &&
                checkPermission(loggedUser.permissions, 'add_own_work_orders') &&
                !checkPermission(loggedUser.permissions, 'view_work_orders')
                    ? 'Mis Órdenes de Trabajo'
                    : 'Órdenes de Trabajo'}
            </h1>

            {/* Mensaje de éxito que se auto-oculta */}
            {showSuccessMessage && (
                <div className='alert alert-success text-center mt-3' role='alert'>
                    Orden de trabajo creada satisfactoriamente.
                </div>
            )}

            {/* Botones de acción */}
            <div className='d-flex mt-3 justify-content-end'>
                {loggedUser && checkPermission(loggedUser.permissions, 'view_work_orders') && (
                    <AddButton type='button' className='btn btn-primary me-2' onClick={() => navigate('/ordenes/historial')}>
                        <RiHistoryLine className='me-2' />
                        Historial
                    </AddButton>
                )}
                {loggedUser && checkPermission(loggedUser.permissions, ['add_work_orders', 'add_own_work_orders']) && (
                    <AddButton type='button' className='btn btn-primary' onClick={() => navigate('/crear-orden')}>
                        <FaPlus className='me-2' />
                        Nueva orden
                    </AddButton>
                )}
            </div>

            {/* Leyenda de estados usando styled-component */}
            {loggedUser && checkPermission(loggedUser.permissions, 'view_work_orders') && (
                <div className='d-flex align-items-center gap-4 mb-3 mt-3' style={{ fontSize: '14px' }}>
                    <span className='d-flex align-items-center'>
                        <OrderStatusLegend color={ORDER_STATUS.CREATED.color} />
                        {ORDER_STATUS.CREATED.label}
                    </span>
                    <span className='d-flex align-items-center'>
                        <OrderStatusLegend color={ORDER_STATUS.APPROVED.color} />
                        {ORDER_STATUS.APPROVED.label}
                    </span>
                    <span className='d-flex align-items-center'>
                        <OrderStatusLegend color={ORDER_STATUS.MAINTENANCE.color} />
                        {ORDER_STATUS.MAINTENANCE.label}
                    </span>
                </div>
            )}

            {/* Leyenda de estados para vista de órdenes propias */}
            {loggedUser &&
                checkPermission(loggedUser.permissions, 'add_own_work_orders') &&
                !checkPermission(loggedUser.permissions, 'view_work_orders') && (
                    <div className='d-flex align-items-center gap-4 mb-3 mt-3' style={{ fontSize: '14px' }}>
                        <span className='d-flex align-items-center'>
                            <OrderStatusLegend color={ORDER_STATUS.CREATED.color} />
                            {ORDER_STATUS.CREATED.label}
                        </span>
                        <span className='d-flex align-items-center'>
                            <OrderStatusLegend color={ORDER_STATUS.APPROVED.color} />
                            {ORDER_STATUS.APPROVED.label}
                        </span>
                    </div>
                )}

            {/* Contenido de la tabla según permisos */}
            {renderTableContent()}
        </div>
    );
};

const ResponsiveTable = styled.table`
    width: 100%;
    border: rgba(0, 0, 0, 0.12) solid 1px;
    border-collapse: collapse;
    margin-top: 10px;

    thead {
        background-color: #eeeeee;
        font-size: 14px;
        font-weight: bold;
    }

    th {
        padding: 10px;
        text-align: left;
        border-right: rgba(0, 0, 0, 0.12) solid 1px;

        &:last-child {
            border-right: none;
        }
    }

    td {
        padding: 10px;
        font-size: 14px;
        border-right: rgba(0, 0, 0, 0.12) solid 1px;
        border-top: rgba(0, 0, 0, 0.12) solid 1px;

        &:last-child {
            border-right: none;
        }
    }

    tbody tr:hover {
        background-color: #f5f5f5;
    }

    @media (max-width: 768px) {
        border: none;

        thead {
            display: none;
        }

        tbody tr {
            display: grid;
            grid-template-columns: auto 1fr;
            gap: 10px;
            padding: 15px;
            margin-bottom: 10px;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 6px;
            background-color: #fff;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        td {
            display: contents;
            border: none;

            &:before {
                content: attr(data-label);
                font-weight: bold;
                font-size: 13px;
                color: #666;
                grid-column: 1;
            }

            &:after {
                content: '';
                grid-column: 2;
            }
        }

        td:nth-child(1):before {
            content: 'N° Orden:';
        }

        td:nth-child(2):before {
            content: 'Placa:';
        }

        td:nth-child(3):before {
            content: 'Observaciones:';
        }

        td:nth-child(4):before {
            content: 'Instrucciones:';
        }

        td:nth-child(1),
        td:nth-child(2),
        td:nth-child(3),
        td:nth-child(4) {
            grid-column: span 2;
            display: grid;
            grid-template-columns: 120px 1fr;
            gap: 10px;
            align-items: start;
            padding: 5px 0;

            &:before {
                grid-column: 1;
            }
        }
    }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding: 10px 0;
    flex-wrap: wrap;
    gap: 10px;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const PaginationInfo = styled.div`
    font-size: 14px;
    color: #666;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    select {
        padding: 5px 10px;
        border: 1px solid #ccc;
        border-radius: 4px;
        font-size: 14px;
    }

    button {
        padding: 5px 15px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
        font-size: 14px;

        &:hover:not(:disabled) {
            background-color: #f5f5f5;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    span {
        font-size: 14px;
        color: #666;
    }

    @media (max-width: 768px) {
        flex-wrap: wrap;
        justify-content: center;
    }
`;

export default Orders;
